import React, { useState, useEffect, useMemo } from "react";
import { numeroBR } from "../numero";
import {
  ChevronRight,
  Info,
  Save,
} from "lucide-react";
import { Btn, Field, Modal, Pill, usarServidor } from "../ui/componentes";
import { brl } from "../ui/formato";

/* ══════════════════════════════════════════════════════════════════
   IMPORTAR ESTOQUE
   Quatro passos, e o estoque só muda no último. Import ruim direto
   no estoque vivo é o que destrói a confiança logo na migração.
   ══════════════════════════════════════════════════════════════════ */


const SINONIMOS = {
  cod: ["codigo", "cod", "codproduto", "referencia", "ref", "sku", "codigopeca"],
  nome: ["descricao", "produto", "peca", "nome", "item", "mercadoria"],
  un: ["unidade", "un", "und"],
  qtd: ["quantidade", "qtd", "qtde", "estoque", "saldo", "estoqueatual", "quant"],
  custo: ["custo", "precocusto", "valorcusto", "custounitario", "vlcusto", "customedio"],
  venda: ["venda", "preco", "precovenda", "valorvenda", "vlvenda", "valor", "precopublico"],
  marca: ["marca", "fabricante"],
  local: ["localizacao", "local", "prateleira", "endereco"],
};
const normalizar = (t) => t.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]/g, "");

function ImportarEstoque({ open, onClose, db, set, toast, acoes }) {
  const { executar, ocupado } = usarServidor(toast);
  const [impId, setImpId] = useState(null);
  const [passo, setPasso] = useState(1);
  const [texto, setTexto] = useState("");
  const [mapa, setMapa] = useState({});
  const [linhas, setLinhas] = useState([]);
  const [cols, setCols] = useState([]);

  useEffect(() => { if (!open) { setPasso(1); setTexto(""); setLinhas([]); setCols([]); } }, [open]);

  const analisar = () => {
    const cru = texto.split(/\r?\n/).filter((l) => l.trim());
    if (cru.length < 2) { toast("Nada para ler", "Cole o cabeçalho e pelo menos uma linha de peça.", "erro"); return; }
    const sep = [";", ",", "\t", "|"].map((s) => [s, cru[0].split(s).length]).sort((a, b) => b[1] - a[1])[0][0];
    const colunas = cru[0].split(sep).map((c, i) => c.trim().replace(/^"|"$/g, "") || `coluna_${i + 1}`);
    const m = {};
    const usadas = new Set();
    for (const [campo, alvos] of Object.entries(SINONIMOS)) {
      const achou = colunas.find((c) => !usadas.has(c) &&
        (alvos.includes(normalizar(c)) || alvos.some((a) => normalizar(c).includes(a))));
      if (achou) { m[campo] = achou; usadas.add(achou); }
    }
    const dados = cru.slice(1).map((l) => {
      const c = l.split(sep).map((x) => x.trim().replace(/^"|"$/g, ""));
      const o = {}; colunas.forEach((col, i) => (o[col] = c[i] ?? "")); return o;
    }).filter((o) => Object.values(o).filter(Boolean).length > 1);
    setCols(colunas); setMapa(m); setLinhas(dados); setPasso(2);
  };

  const conferidas = useMemo(() => linhas.map((l, i) => {
    const g = (c) => (mapa[c] ? (l[mapa[c]] ?? "").trim() : "");
    const cod = g("cod"), nome = g("nome");
    const qtd = numeroBR(g("qtd")) ?? 0;
    const custo = numeroBR(g("custo"));
    const venda = numeroBR(g("venda"));
    const existente = db.pecas.find((p) => (cod && p.cod === cod) ||
      (nome && p.nome.toLowerCase() === nome.toLowerCase()));
    const probs = [];
    if (!nome) probs.push("Sem nome de peça");
    if (custo != null && venda != null && venda > 0 && custo > venda) probs.push("Custo maior que a venda — colunas podem estar trocadas");
    if (custo == null && venda == null) probs.push("Sem custo nem preço");
    return { i, cod, nome, un: g("un") || "UN", marca: g("marca"), local: g("local"),
             qtd, custo: custo ?? 0, venda: venda ?? 0, existente,
             acao: !nome ? "erro" : probs.some((p) => p.includes("trocadas")) ? "conflito" : existente ? "atualizar" : "criar",
             probs };
  }), [linhas, mapa, db.pecas]);

  const resumo = useMemo(() => ({
    criar: conferidas.filter((c) => c.acao === "criar").length,
    atualizar: conferidas.filter((c) => c.acao === "atualizar").length,
    conflito: conferidas.filter((c) => c.acao === "conflito").length,
    erro: conferidas.filter((c) => c.acao === "erro").length,
    valor: conferidas.filter((c) => ["criar", "atualizar"].includes(c.acao)).reduce((s, c) => s + c.qtd * c.custo, 0),
  }), [conferidas]);

  /**
   * A conferência acontece no servidor.
   *
   * A prévia local ajuda a ver o mapeamento, mas quem decide criar ou
   * atualizar é o backend — ele conhece o estoque inteiro, inclusive o que
   * outra pessoa cadastrou nos últimos cinco minutos.
   *
   * E é reversível: se a planilha estiver errada, `reverter` estorna por
   * movimento contrário em vez de apagar.
   */
  const aplicar = () => {
    const csv = [cols.join(";"), ...linhas.map((l) => cols.map((c) => l[c] ?? "").join(";"))].join("\n");
    const b64 = btoa(unescape(encodeURIComponent(csv)));
    const mapeamento = Object.entries(mapa)
      .filter(([, v]) => v)
      .map(([campo, coluna]) => ({
        campo: { cod: "codigo", nome: "descricao", un: "unidade", qtd: "quantidade",
                 custo: "custo", venda: "preco", marca: "marca", local: "localizacao" }[campo],
        coluna,
      }))
      .filter((m) => m.campo);

    executar(
      async () => {
        const est = await acoes.importarEstoque(b64, "planilha.csv", mapeamento);
        return acoes.aplicarImportacao(est.id);
      },
      (r) => {
        onClose();
        toast("Estoque importado",
          `${r.criadas} peça(s) criadas e ${r.atualizadas} atualizada(s) · ${brl(r.valorEntrada)} em estoque.`);
      },
      "Não deu para importar");
  };

  const EXEMPLO = ["Cod.Produto;Descricao;Un;Qtde;Vl.Custo;Preco Venda",
    "FLT-0231;Filtro de oleo do motor;UN;22;18,90;42,00",
    "AMT-TRA-01;Amortecedor traseiro;PC;4;198,00;410,00",
    "COR-ALT-05;Correia do alternador;PC;7;42,50;95,00",
    "PAL-LIMP-22;Palheta limpador 22 pol;PC;12;28,00;65,00"].join("\n");

  return (
    <Modal open={open} onClose={onClose} w="max-w-3xl"
      title="Importar estoque de outro sistema"
      sub={["Cole os dados", "Confira o mapeamento", "Revise o que vai acontecer", "Aplicar"][passo - 1]}
      footer={<>
        <Btn variant="g" onClick={onClose}>Cancelar</Btn>
        {passo > 1 && <Btn variant="o" onClick={() => setPasso(passo - 1)}>Voltar</Btn>}
        {passo === 1 && <Btn variant="p" icon={ChevronRight} onClick={analisar}>Analisar</Btn>}
        {passo === 2 && <Btn variant="p" icon={ChevronRight} onClick={() => setPasso(3)}>Conferir</Btn>}
        {passo === 3 && <Btn variant="ok" icon={Save} onClick={aplicar}>Aplicar ao estoque</Btn>}
      </>}>

      <div className="flex items-center gap-1.5 mb-4">
        {[1, 2, 3].map((n) => (
          <React.Fragment key={n}>
            <span className="mono flex items-center justify-center" style={{ width: 24, height: 24, borderRadius: "50%",
              background: passo >= n ? "var(--ink)" : "var(--el)", color: passo >= n ? "#fff" : "var(--mut2)",
              fontSize: 11, fontWeight: 600, border: "1px solid var(--line)" }}>{n}</span>
            {n < 3 && <span style={{ flex: 1, height: 2, background: passo > n ? "var(--ink)" : "var(--line)" }} />}
          </React.Fragment>
        ))}
      </div>

      {passo === 1 && (
        <div className="grid gap-3">
          <div className="flex items-start gap-2.5 p-3 text-sm" style={{ background: "var(--gulf-l)", border: "1px solid #BEE0EC", borderRadius: "var(--r)" }}>
            <Info size={15} className="t-gulf mt-0.5 flex-none" />
            <span>Exporte a listagem de estoque do sistema atual em CSV ou Excel e cole aqui. Nada é alterado agora — você confere tudo antes.</span>
          </div>
          <Field label="Dados da planilha" hint="Primeira linha com os nomes das colunas.">
            <textarea className="inp mono" rows={9} style={{ fontSize: 12 }} value={texto}
              onChange={(e) => setTexto(e.target.value)} placeholder="Cod;Descricao;Qtde;Custo;Venda" />
          </Field>
          <Btn variant="g" size="xs" onClick={() => setTexto(EXEMPLO)}>Usar um exemplo para testar</Btn>
        </div>
      )}

      {passo === 2 && (
        <div className="grid gap-3">
          <div className="text-sm t-mut">
            {linhas.length} linha(s) lidas. Confira se cada coluna foi para o lugar certo —
            "Valor" pode ser custo ou venda, e nenhum sistema adivinha isso sem olhar os números.
          </div>
          <div className="grid gap-2.5" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))" }}>
            {[["cod", "Código"], ["nome", "Nome da peça"], ["un", "Unidade"], ["qtd", "Quantidade"],
              ["custo", "Custo"], ["venda", "Preço de venda"], ["marca", "Marca"], ["local", "Prateleira"]].map(([k, l]) => (
              <Field key={k} label={l}>
                <select className="inp" value={mapa[k] ?? ""} onChange={(e) => setMapa({ ...mapa, [k]: e.target.value })}>
                  <option value="">— não importar —</option>
                  {cols.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            ))}
          </div>
        </div>
      )}

      {passo === 3 && (
        <div className="grid gap-3">
          <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(110px,1fr))" }}>
            {[["Criar", resumo.criar, "ok"], ["Atualizar", resumo.atualizar, "info"],
              ["Conflito", resumo.conflito, "sig"], ["Erro", resumo.erro, "crit"]].map(([l, n, t]) => (
              <div key={l} className="bg-el brd p-2.5 text-center" style={{ borderRadius: "var(--r)" }}>
                <div className="dsp mono" style={{ fontSize: 20 }}>{n}</div>
                <Pill t={t}>{l}</Pill>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between p-3" style={{ background: "var(--el)", border: "1px solid var(--line)", borderRadius: "var(--r)" }}>
            <span className="text-sm t-mut">Valor que entra no estoque, a custo</span>
            <span className="mono font-semibold" style={{ fontSize: 17 }}>{brl(resumo.valor)}</span>
          </div>
          <div className="scroll" style={{ maxHeight: 300, overflowY: "auto", border: "1px solid var(--line)", borderRadius: "var(--r)" }}>
            <table className="tbl">
              <thead><tr><th>Ação</th><th>Peça</th><th className="num">Qtd</th><th className="num">Custo</th><th className="num">Venda</th><th>Observação</th></tr></thead>
              <tbody>
                {conferidas.map((c) => (
                  <tr key={c.i}>
                    <td><Pill t={c.acao === "criar" ? "ok" : c.acao === "atualizar" ? "info" : c.acao === "conflito" ? "sig" : "crit"}>{c.acao}</Pill></td>
                    <td><span className="text-sm font-medium">{c.nome || "—"}</span>
                      <span className="block text-xs t-mut2 mono">{c.cod}</span></td>
                    <td className="num text-sm">{c.qtd}</td>
                    <td className="num text-sm">{brl(c.custo)}</td>
                    <td className="num text-sm">{brl(c.venda)}</td>
                    <td className="text-xs t-mut">{c.probs.join(" · ") || (c.existente ? `Estoque ${c.existente.qtd} → ${c.qtd}` : "")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}

export { SINONIMOS, normalizar, ImportarEstoque };
